/**
 * @Description 选址地图青春版-左侧-品牌门店走势公共组件，该组件只管渲染不管逻辑
 * 国省市视角：全国/省份/城市
 * 分公司视角：全国/分公司/城市
 */

import { FC, memo, useEffect, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { isNotEmptyAny } from '@lhb/func';
import { Typography } from 'antd';
import { useBigdataFn } from '@/common/utils/bigdata';
import cs from 'classnames';
import dayjs from 'dayjs';
import styles from './index.module.less';
import MoreTabs from '@/common/components/business/MoreTabs';
import V2LineChart from '@/common/components/Charts/V2LineChart';

interface LeftConOfBrandTendencyProps {
  brandData: any[]; // 品牌数据
  isCityView?: boolean; // 是否是城市视角
  showCompetitionBadge?: boolean; // 是否展示竞合关系角标
};

const LeftConOfBrandTendency: FC<LeftConOfBrandTendencyProps> = ({
  brandData,
  isCityView = false,
  showCompetitionBadge = false,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const [activeKey, setActiveKey] = useState('');
  const [xAxisDataLine, setXAxisDataLine] = useState([]);
  const [seriesDataLine, setSeriesDataLine] = useState([]);
  const [openStore, setOpenStore] = useState(0); // 在营门店
  const [cityCount, setCityCount] = useState(0); // 覆盖城市
  const [activeCurrent, setActiveCurrent] = useState<any>({}); // 当前选中项
  const [yAxis, setYAxis] = useState<any>({});
  const [updateKey, setUpdateKey] = useState<number>(0);
  useEffect(() => {
    if (brandData?.length) {
      setActiveKey(brandData[0]?.brandId?.toString?.());
      setActiveCurrent(brandData[0]);
    }
  }, [brandData]);

  useEffect(() => {
    const activeCompetitor = brandData.find((item) => item?.brandId?.toString?.() === activeKey);
    if (isNotEmptyAny(activeCompetitor)) {
      const xAxisData = activeCompetitor?.monthStatisticList?.map((item) => dayjs(item.statisticDate).format('M月')) || [];
      const seriesData = activeCompetitor?.monthStatisticList?.map((item) => item.storeNumber) || [];
      setYAxis({
        min: methods.getTargetVal(seriesData, 'min'),
        max: methods.getTargetVal(seriesData, 'max'),
      });
      setXAxisDataLine(xAxisData);
      setSeriesDataLine(seriesData);
      setOpenStore(activeCompetitor.monthStatisticList?.[activeCompetitor.monthStatisticList?.length - 1]?.storeNumber || 0);
      setCityCount(activeCompetitor.cityCount || 0);
      setTimeout(() => {
        setUpdateKey((state) => state + 1);
      }, 0);
    }
  }, [activeKey, brandData]);

  const methods = useMethods({
    handleBigdataFn: () => {
      bigdataFn('8fc6eaab-5803-4f50-9ac6-8df11de2d139', '品牌门店趋势-tab按钮', '点击了品牌门店趋势-tab按钮', { op_block: '选址地图青春版', op_event: ``, });
    },
    getTargetVal(value, type: 'min' | 'max') {
      if (!value?.length) return 0;
      const min = Math.min(...value);
      const max = Math.max(...value);
      // const differenceVal = max - min;
      // if (differenceVal < 5) {
      //   if (type === 'min') return min;
      //   if (type === 'max') return max + 1;
      // }
      // if (type === 'min') {
      //   return Math.floor(min * 0.97);
      // } else {
      //   return Math.ceil(max * 1.03);
      // }
      if (type === 'min') {
        return min;
      } else {
        return max;
      }
    },
  });

  return (
    <>
      <MoreTabs
        tabs={brandData}
        tabName='brandName'
        tabId='brandId'
        onChange={(target) => {
          setActiveCurrent(target);
          setActiveKey(target?.brandId?.toString?.() || '');
        }}
        handleBigdataFn={methods.handleBigdataFn}
      />
      <div className={styles.brandInfoWrap}>
        <div className={styles.brandInfo}>
          <div className={cs(styles.infoTop, isCityView ? styles.cityMode : '')}>
            <div className={styles.infoLeft}>
              <img src={activeCurrent.logo || `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/youth/self-brand.png`} className={styles.img} />
              <div className={styles.activeTitle}>
                <Typography.Text style={{ width: '100%' }} ellipsis={{ tooltip: activeCurrent?.brandName }}>{ activeCurrent?.brandName }</Typography.Text>
              </div>
            </div>
            <div className={styles.infoRight}>
              {
                isCityView ? <></> : <div className={styles.infoItem}>
                  <div className={styles.infoNum}>{cityCount}</div>
                  <div className={styles.infoLabel}>覆盖城市</div>
                </div>
              }
              <div className={styles.infoItem}>
                <div className={styles.infoNum}>{openStore}</div>
                <div className={styles.infoLabel}>在营门店</div>
              </div>
            </div>
          </div>
        </div>
        {showCompetitionBadge && !!activeCurrent?.competitionName && (
          <div className={styles.competitionBadge}>{activeCurrent.competitionName}</div>
        )}
      </div>
      {
        seriesDataLine?.length ? <div className={styles.brandChart}>
          <V2LineChart
            key={updateKey}
            wrapperClassName={styles.chartContent}
            title='近一年门店趋势'
            height={218}
            resizable
            xAxisData={xAxisDataLine}
            seriesData={[{
              // name: '门店数',
              data: seriesDataLine
            }]}
            config={{
              animation: false,
              yAxis: {
                minInterval: 1, // 保证是整数
                ...yAxis,
                axisLabel: {
                  formatter: (value) => {
                    return value;
                  }
                }
              },
              title: {
                textStyle: {
                  color: '#222222',
                  fontWeight: 600,
                  fontFamily: 'PingFangSC-Semibold',
                  fontSize: 12,
                  lineHeight: 22,
                },
                top: 0,
              },
              grid: {
                top: 38
              }
            }}
          />
        </div> : <></>
      }
    </>
  );
};

export default memo(LeftConOfBrandTendency);
